import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronRight, FileText, FileCheck, FileSignature, Shield, Volume2, VolumeX } from 'lucide-react';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 100%;
`;

const TypeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #2d2d2d;
  border: 1px solid #4a4a4a;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #e0e0e0;
  transition: all 0.2s;
  text-transform: capitalize;
  width: 100%;
  text-align: left;
  
  &:hover {
    background: #3a3a3a;
    border-color: #5a5a5a;
  }
`;

const DocumentCount = styled.span`
  background: #3b82f6;
  color: white;
  border-radius: 10px;
  padding: 0.1rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ExpandableContent = styled.div`
  margin-left: 1rem;
  border-left: 2px solid #4a4a4a;
  padding-left: 1rem;
  padding-top: 0.75rem;
  display: ${props => props.isExpanded ? 'block' : 'none'};
`;

const DocumentItem = styled.div`
  margin-bottom: 0.75rem;
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const DocumentTitle = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: #e0e0e0;
  word-break: break-word;
`;

const FileCircle = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background-color: #e74c3c;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
`;

const FileCircles = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const MuteToggleButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.muted ? 'rgba(255, 255, 255, 0.1)' : '#3b82f6'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  margin-left: 0.25rem;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    background-color: ${props => props.muted ? 'rgba(255, 255, 255, 0.2)' : '#2563eb'};
  }
`;

const ChunkItem = styled.div`
  margin-left: 0;
  margin-bottom: 0.5rem;
  padding-left: 0.5rem;
`;

const ChunkTitle = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: #b0b0b0;
  margin-right: 0.5rem;
`;

const ChunkFileCircles = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.25rem;
`;

// Default file type configuration
const defaultFileTypeConfig = {
  color: '#6b7280',
  icon: FileText,
  label: 'Document'
};

// Specific file type configurations
const specificFileTypeConfigs = {
  master: {
    color: '#3b82f6',
    icon: FileText,
    label: 'Master'
  },
  proposal: {
    color: '#8b5cf6',
    icon: FileCheck,
    label: 'Proposal'
  },
  note: {
    color: '#10b981',
    icon: FileSignature,
    label: 'Note'
  },
  security: {
    color: '#f59e0b',
    icon: Shield,
    label: 'Security'
  },
  additional: {
    color: '#6366f1',
    icon: FileText,
    label: 'Additional'
  }
};

// Function to get configuration for a document type
const getFileTypeConfig = (type) => {
  return specificFileTypeConfigs[type] || {
    ...defaultFileTypeConfig,
    label: type.charAt(0).toUpperCase() + type.slice(1)
  };
};

// Helper function to detect if a document is a chunk
const isChunk = (doc) => {
  const filename = doc.original_filename || doc.fileName || doc.name || '';
  return filename.includes('_chunk_');
};

// Helper function to extract base filename from chunk
const getBaseFilename = (chunkFilename) => {
  const filename = chunkFilename || '';
  const chunkIndex = filename.indexOf('_chunk_');
  if (chunkIndex === -1) return filename;
  return filename.substring(0, chunkIndex);
};

// Helper function to extract page range from chunk filename
const getPageRange = (chunkFilename) => {
  const filename = chunkFilename || '';
  const chunkMatch = filename.match(/_chunk_(\d+)-(\d+)\.pdf$/);
  if (chunkMatch) {
    return `Pages ${chunkMatch[1]} - ${chunkMatch[2]}`;
  }
  return 'Pages Unknown';
};

const fileColors = {
  pdf: '#e74c3c',
  csv: '#27ae60', 
  json: '#f39c12',
  txt: '#95a5a6',
  jpg: '#8e44ad',
  jpeg: '#8e44ad',
  png: '#3498db',
  tif: '#9b59b6',
  tiff: '#9b59b6',
  xml: '#34495e'
};

const getFileExtension = (doc) => {
  if (!doc) return 'txt';
  
  // If document has a type field, use it to determine the actual content type
  if (doc.type) {
    switch (doc.type) {
      case 'keyvalues':
        return 'json'; // KeyValues are always JSON
      case 'textract':
        return 'json'; // Textract results are JSON
      case 'source':
        // For source files, check the actual filename extension
        const fileName = doc.fileName || doc.name || '';
        const extension = fileName.split('.').pop().toLowerCase();
        
        // Map common extensions to their actual content types
        if (extension === 'pdf') return 'pdf';
        if (extension === 'csv') return 'csv';
        if (extension === 'json') return 'json';
        if (['txt', 'text'].includes(extension)) return 'txt';
        if (['xlsx', 'xls'].includes(extension)) return 'csv'; // Treat Excel as CSV for display
        if (['jpg', 'jpeg', 'png'].includes(extension)) return 'jpg';
        if (['tif', 'tiff'].includes(extension)) return 'tif';
        if (extension === 'xml') return 'xml';
        
        return extension || 'txt';
      default:
        return 'txt';
    }
  }
  
  // Fallback to filename extension if no type field
  const fileName = doc.fileName || doc.name || '';
  return fileName.split('.').pop().toLowerCase() || 'txt';
};

function HoverDocumentCircles({ 
  documentsByType = {},
  onPreviewDocument,
  onToggleMute
}) {
  
  const [expandedTypes, setExpandedTypes] = useState({});
  const [mutedFiles, setMutedFiles] = useState({});

  // Initialize mutedFiles state from the ignored property of documents
  useEffect(() => {
    const initialMutedFiles = {};
    
    Object.values(documentsByType).forEach(documents => {
      documents.forEach(doc => {
        const key = doc.id || doc.original_filename || doc.fileName || doc.name;
        if (key) {
          initialMutedFiles[key] = doc.ignored || false;
        }
      });
    });
    
    setMutedFiles(initialMutedFiles);
  }, [documentsByType]);

  // Group documents by type, then by name within each type
  const documentGroups = Object.entries(documentsByType).map(([type, documents]) => {
    // Separate chunks from regular documents
    const chunks = documents.filter(isChunk);
    const regularDocs = documents.filter(doc => !isChunk(doc));
    
    // Group chunks by their base filename
    const chunksByBase = {};
    chunks.forEach(chunk => {
      const baseFilename = getBaseFilename(chunk.original_filename || chunk.fileName || chunk.name);
      if (!chunksByBase[baseFilename]) {
        chunksByBase[baseFilename] = [];
      }
      chunksByBase[baseFilename].push(chunk);
    });
    
    // Group regular documents by their base name (without type suffix)
    const documentsByName = {};
    regularDocs.forEach(doc => {
      const baseName = doc.fileName || doc.name || '';
      if (!documentsByName[baseName]) {
        documentsByName[baseName] = [];
      }
      documentsByName[baseName].push(doc);
    });
    
    return {
      type,
      documentsByName: Object.entries(documentsByName).map(([name, docs]) => ({
        name,
        documents: docs,
        isChunked: false
      })),
      chunksByName: Object.entries(chunksByBase).map(([baseName, chunkDocs]) => ({
        name: baseName,
        documents: chunkDocs,
        isChunked: true
      }))
    };
  });

  const toggleType = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handlePreview = (document) => {
    if (onPreviewDocument) {
      onPreviewDocument(document);
    }
  };

  const toggleMute = (doc) => {
    const key = doc.id || doc.original_filename || doc.fileName || doc.name;
    const newMutedState = !mutedFiles[key];
    setMutedFiles(prev => ({
      ...prev,
      [key]: newMutedState
    }));
    
    if (onToggleMute) {
      // Use the same identifier that the parent component expects
      const fileName = doc.original_filename || doc.fileName || doc.name;
      onToggleMute(fileName, newMutedState);
    }
  };

  return (
    <Container>
      {documentGroups.map(({ type, documentsByName, chunksByName }) => {
        const totalItems = documentsByName.length + chunksByName.length;
        if (totalItems === 0) return null;
        
        const config = getFileTypeConfig(type);
        const IconComponent = config.icon;
        const isExpanded = expandedTypes[type];
        
        return (
          <div key={type}>
            <TypeButton onClick={() => toggleType(type)}>
              {isExpanded ? <ChevronDown size={16} color="#e0e0e0" /> : <ChevronRight size={16} color="#e0e0e0" />}
              <IconComponent size={16} color="#e0e0e0" />
              <span>{config.label}</span>
              <DocumentCount>
                {totalItems}
              </DocumentCount>
            </TypeButton>
            
            <ExpandableContent isExpanded={isExpanded}>
              {/* Render regular documents */}
              {documentsByName.map(({ name, documents }, nameIndex) => (
                <DocumentItem key={`regular-${nameIndex}`}>
                  <DocumentHeader>
                    <DocumentTitle>{name}</DocumentTitle>
                  </DocumentHeader>
                  <FileCircles>
                    {/* Show source buttons for regular documents if they have S3 keys */}
                    {documents[0] && (
                      <>
                        {/* Source file badge - show appropriate type based on file extension */}
                        {documents[0].s3_source_key && (() => {
                          const fileName = documents[0].fileName || documents[0].name || '';
                          const extension = fileName.split('.').pop()?.toLowerCase();
                          const isJpeg = extension === 'jpg' || extension === 'jpeg';
                          
                          return (
                            <FileCircle
                              style={{ backgroundColor: isJpeg ? fileColors.jpg : fileColors.pdf }}
                              onClick={() => handlePreview(documents[0])}
                              title={`${fileName} (source)`}
                            >
                              {isJpeg ? 'JPG' : 'PDF'}
                            </FileCircle>
                          );
                        })()}
                        
                        {/* Textract JSON badge */}
                        {documents[0].s3_textract_key && (
                          <FileCircle
                            style={{ backgroundColor: fileColors.json }}
                            onClick={() => handlePreview({ ...documents[0], type: 'textract' })}
                            title={`${documents[0].fileName || documents[0].name} (textract)`}
                          >
                            JSON
                          </FileCircle>
                        )}
                        
                        {/* KeyValues JSON badge */}
                        {documents[0].s3_keyvalues_key && (
                          <FileCircle
                            style={{ backgroundColor: fileColors.json }}
                            onClick={() => handlePreview({ ...documents[0], type: 'keyvalues' })}
                            title={`${documents[0].fileName || documents[0].name} (keyvalues)`}
                          >
                            JSON
                          </FileCircle>
                        )}
                      </>
                    )}
                    <MuteToggleButton
                      muted={mutedFiles[documents[0]?.id || documents[0]?.original_filename || documents[0]?.fileName || documents[0]?.name]}
                      onClick={() => toggleMute(documents[0])}
                      title={mutedFiles[documents[0]?.id || documents[0]?.original_filename || documents[0]?.fileName || documents[0]?.name] ? 'Unmute file' : 'Mute file'}
                    >
                      {mutedFiles[documents[0]?.id || documents[0]?.original_filename || documents[0]?.fileName || documents[0]?.name] ? <VolumeX size={12} /> : <Volume2 size={12} />}
                    </MuteToggleButton>
                  </FileCircles>
                </DocumentItem>
              ))}
              
              {/* Render chunked documents */}
              {chunksByName.map(({ name, documents }, nameIndex) => {
                // Group chunks by their page range (since each chunk has multiple file types)
                const chunksByPageRange = {};
                documents.forEach(chunk => {
                  const pageRange = getPageRange(chunk.original_filename || chunk.fileName || chunk.name);
                  if (!chunksByPageRange[pageRange]) {
                    chunksByPageRange[pageRange] = [];
                  }
                  chunksByPageRange[pageRange].push(chunk);
                });
                
                return (
                  <DocumentItem key={`chunked-${nameIndex}`}>
                    <DocumentHeader>
                      <DocumentTitle>{name}</DocumentTitle>
                    </DocumentHeader>
                    {Object.entries(chunksByPageRange).map(([pageRange, chunkFiles], pageIndex) => {
                      // Find the main chunk file (the one with the original filename)
                      const mainChunk = chunkFiles.find(chunk => 
                        chunk.original_filename && chunk.original_filename.includes('_chunk_')
                      ) || chunkFiles[0];
                      
                      const key = mainChunk.id || mainChunk.original_filename || mainChunk.fileName || mainChunk.name;
                      
                      
                      return (
                        <ChunkItem key={pageIndex}>
                          <ChunkTitle>{pageRange}</ChunkTitle>
                          <ChunkFileCircles>
                            {/* PDF badge - only show if there's a source file */}
                            {mainChunk.s3_source_key && (() => {
                              const fileName = mainChunk.original_filename || '';
                              const extension = fileName.split('.').pop()?.toLowerCase();
                              const isJpeg = extension === 'jpg' || extension === 'jpeg';
                              
                              return (
                                <FileCircle
                                  style={{ backgroundColor: isJpeg ? fileColors.jpg : fileColors.pdf }}
                                  onClick={() => handlePreview(mainChunk)}
                                  title={`${fileName} (source)`}
                                >
                                  {isJpeg ? 'JPG' : 'PDF'}
                                </FileCircle>
                              );
                            })()}
                            
                            {/* Textract JSON badge */}
                            {mainChunk.s3_textract_key && (
                              <FileCircle
                                style={{ backgroundColor: fileColors.json }}
                                onClick={() => handlePreview({ ...mainChunk, type: 'textract' })}
                                title={`${mainChunk.original_filename} (textract)`}
                              >
                                JSON
                              </FileCircle>
                            )}
                            
                            {/* KeyValues JSON badge */}
                            {mainChunk.s3_keyvalues_key && (
                              <FileCircle
                                style={{ backgroundColor: fileColors.json }}
                                onClick={() => handlePreview({ ...mainChunk, type: 'keyvalues' })}
                                title={`${mainChunk.original_filename} (keyvalues)`}
                              >
                                JSON
                              </FileCircle>
                            )}
                            
                            {/* Single mute button per chunk */}
                            <MuteToggleButton
                              muted={mutedFiles[key]}
                              onClick={() => toggleMute(mainChunk)}
                              title={mutedFiles[key] ? 'Unmute chunk' : 'Mute chunk'}
                            >
                              {mutedFiles[key] ? <VolumeX size={12} /> : <Volume2 size={12} />}
                            </MuteToggleButton>
                          </ChunkFileCircles>
                        </ChunkItem>
                      );
                    })}
                  </DocumentItem>
                );
              })}
            </ExpandableContent>
          </div>
        );
      })}
    </Container>
  );
}

export default HoverDocumentCircles;